import type { FC } from 'react';
import React, { useRef, useCallback, useEffect } from 'react';
import { useForm } from 'react-final-form';
import cn from 'classnames';
import { sendEvent } from '@alfa-bank/site-helpers-common/browser/send-event';

import { ERROR, INITIAL } from '../../config';
import { useFormContext } from '../../components/form/context';

import type { TSmsLayout } from './models';
import { useValidateCode } from './hooks/use-validate-code';
import { INVALID_TOKEN, PENDING, REJECTED, RESOLVED } from './hooks/config';

const getError = (error?: string) => {
    switch (error) {
        case 'sms_code_is_incorrect':
            return 'Неверный код подтверждения';
        case 'sms_code_has_expired':
            return 'Срок действия кода истек, запросите новый';
        case 'Exceeded the number of attempts to enter the code':
            return 'Превышено количество запросов в сутки';
        default:
            return 'Что-то пошло не так, попробуйте позже';
    }
};

export const SmsLayout: FC<TSmsLayout> = ({
    Aligner,
    CodeInputField,
    Link,
    Text,
    isMobile,
    styles,
}) => {
    const {
        requestCode,
        requestCodeState: {
            data: { messageId, passwordLifetime } = {},
            error: requestCodeError,
            status: requestCodeStatus,
        },
        updateStatus,
        formId,
        setToken,
    } = useFormContext();
    const {
        request: validateCode,
        resetState: resetValidateCodeState,
        state: validateCodeState,
    } = useValidateCode();

    const ref = useRef<HTMLDivElement>(null);
    const { error: validateCodeError, status: validateCodeStatus } = validateCodeState;
    const error = requestCodeError || validateCodeError;
    const isPending =
        requestCodeStatus === PENDING ||
        validateCodeStatus === PENDING ||
        validateCodeStatus === RESOLVED; // продолжаем крутить спиннер, если код провалидирован успешно

    const form = useForm();
    const phoneNumber = String(form.getState().values.phone);

    const handleComplete = (code: string) => {
        if (messageId) {
            validateCode(code, phoneNumber, messageId);
        }
    };

    const handleRetry = (tokenCaptcha: string | null) => {
        requestCode(phoneNumber, tokenCaptcha);
        setToken(tokenCaptcha);
    };

    const sendInputCodeResultEvent = useCallback(
        (result: 'success' | 'fail') => {
            sendEvent({
                event: 'site_formInputCode_result',
                form_id: formId,
                status: result,
            });
        },
        [formId]
    );

    useEffect(() => {
        if (isMobile && ref.current) {
            ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        sendEvent({
            event: 'site_formInputCode_show',
            form_id: formId,
        });
    }, [formId]);

    useEffect(() => {
        if (validateCodeStatus === RESOLVED && !validateCodeError) {
            sendInputCodeResultEvent('success');
            form.submit();
        } else if (validateCodeStatus === REJECTED || validateCodeError) {
            sendInputCodeResultEvent('fail');
        }
    }, [validateCodeStatus, validateCodeError, form, sendInputCodeResultEvent]);

    useEffect(() => {
        if (requestCodeStatus === INVALID_TOKEN) {
            updateStatus(ERROR);
        }
    }, [requestCodeStatus, updateStatus]);

    const onChangePhoneClick = () => {
        updateStatus(INITIAL);
        sendEvent({ event: 'site_formInputCodeLink_click', form_id: formId });
    };

    return (
        <Aligner align="center">
            <div ref={ref} />
            <Text
                className={cn(styles.external, styles.header)}
                fontPreset={isMobile ? 'systemSmall' : 'systemNormal'}
                tag="h2"
            >
                Введите код&nbsp;из&nbsp;сообщения
            </Text>
            <span className={styles.phoneBlock}>
                <Text className={cn(styles.external, styles.phoneText)} tag="span">
                    {`Код отправлен на \n${phoneNumber}`}
                </Text>
            </span>
            <CodeInputField
                countdownTime={passwordLifetime}
                error={error && getError(error)}
                isPending={isPending}
                name="otpCode"
                onComplete={handleComplete}
                onResetError={resetValidateCodeState}
                onRetry={handleRetry}
            />
            <div style={{ height: 24 }} />
            <Link className={styles.changeNumberLink} onClick={onChangePhoneClick}>
                Изменить номер
            </Link>
        </Aligner>
    );
};
